import { useEffect, useState } from "react";
import { Gamepad2, Loader2, Megaphone, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Switch } from "@/components/ui/switch";
import { GAMES } from "@/components/admin/TxKit";
import { InfoPopupCard } from "@/components/member/InfoPopup";

const GameToggles = ({ canManage }) => {
  const { games_enabled: ge = {}, reload } = useSiteSettings();
  const [busy, setBusy] = useState("");
  const toggle = async (g, on) => {
    setBusy(g);
    try {
      await api.put("/admin/games", { [g]: on });
      await reload();
      toast.success(`${GAMES[g]} ${on ? "ON — tampil di website" : "OFF — disembunyikan dari website"}`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengubah status game"));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="rk-glass rounded-2xl p-6" data-testid="games-toggle-card">
      <h3 className="flex items-center gap-2 text-lg font-bold"><Gamepad2 size={18} className="text-[#e5b80b]" /> Status Game di Website</h3>
      <p className="mt-1 text-xs text-zinc-500">Game yang OFF tidak tampil di website, member tidak bisa top up maupun bongkar game tersebut. Minimal 1 game harus ON.</p>
      <div className="mt-5 space-y-3">
        {Object.entries(GAMES).map(([g, label]) => {
          const on = ge[g] !== false;
          return (
            <div key={g} className="flex items-center justify-between rounded-xl border border-white/5 bg-[#0d0d12] px-4 py-3" data-testid={`game-toggle-row-${g}`}>
              <div>
                <div className="font-bold">{label}</div>
                <div className={`text-xs font-semibold ${on ? "text-emerald-400" : "text-rose-400"}`} data-testid={`game-status-${g}`}>{on ? "ON · Tersedia untuk member" : "OFF · Disembunyikan"}</div>
              </div>
              {busy === g ? <Loader2 size={16} className="animate-spin text-zinc-400" /> : null}
              <Switch checked={on} disabled={!canManage || !!busy} onCheckedChange={(v) => toggle(g, v)} data-testid={`game-toggle-${g}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PopupForm = ({ canManage }) => {
  const { popup, reload } = useSiteSettings();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (popup) setF(popup);
  }, [popup]);
  if (!f) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/popup", f);
      await reload();
      toast.success("Popup informasi disimpan");
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan popup"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="rk-glass rounded-2xl p-6" data-testid="popup-form">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-bold"><Megaphone size={18} className="text-[#e5b80b]" /> Popup Informasi</h3>
          <p className="mt-1 text-xs text-zinc-500">Muncul saat member membuka website (1x per kunjungan). Link https:// otomatis bisa diklik.</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-bold">
          <span className={f.enabled ? "text-emerald-400" : "text-zinc-500"} data-testid="popup-status">{f.enabled ? "ON" : "OFF"}</span>
          <Switch checked={f.enabled} disabled={!canManage} onCheckedChange={(v) => setF((x) => ({ ...x, enabled: v }))} data-testid="popup-enabled-switch" />
        </label>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="block"><span className="text-xs uppercase tracking-widest text-zinc-500">Judul</span>
            <input value={f.title} onChange={set("title")} disabled={!canManage} maxLength={120} className="rk-input mt-1" data-testid="popup-title-input" /></label>
          <label className="block"><span className="text-xs uppercase tracking-widest text-zinc-500">Isi Pesan</span>
            <textarea value={f.body} onChange={set("body")} disabled={!canManage} maxLength={2000} rows={8} className="rk-input mt-1 resize-y" data-testid="popup-body-input" /></label>
          <label className="block"><span className="text-xs uppercase tracking-widest text-zinc-500">Teks Tombol</span>
            <input value={f.button_text} onChange={set("button_text")} disabled={!canManage} maxLength={40} className="rk-input mt-1" data-testid="popup-button-input" /></label>
        </div>
        <div>
          <div className="mb-2 text-xs uppercase tracking-widest text-zinc-500">Preview</div>
          <div className="rounded-2xl bg-black/60 p-4"><InfoPopupCard p={f} onClose={() => {}} tid="popup-preview" /></div>
        </div>
      </div>
      {canManage ? (
        <button type="submit" disabled={busy || !f.button_text.trim()} className="rk-btn-primary mt-5 inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="popup-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan Popup
        </button>
      ) : null}
    </form>
  );
};

export const GamesPopupPanel = () => {
  const { can } = useAuth();
  const canManage = can("settings", "manage");
  return (
    <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]" data-testid="games-popup-panel">
      <GameToggles canManage={canManage} />
      <PopupForm canManage={canManage} />
    </section>
  );
};
